import React, { useContext, useEffect, useState } from "react";
import { Box, Tab, Stack } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { AppContext } from "../context";
import { pathJoinUrl } from "@smfa/utils/src";
import {
  getInvolvedBanner,
  addEditGetInvolvedsection,
  addEditGetInvolved,
  getInvolvedsection,
} from "../actions/membership.actions";
import FileUploadArea from "../components/common/FileUploadArea";
import LimitedTextField from "../components/common/LimitedTextField";
import FormActionBar from "../components/common/FormActionBar";
import ToolBody from "../components/common/ToolBody";

const GetInvolvedMembership = () => {
  const { storage } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState("1");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Banner state
  const [bannerData, setBannerData] = useState({
    banner_section_id: 0,
    banner_title: "",
    banner_title_error: false,
    banner_title_maxlenth: 30,
    banner_image: "",
    banner_image_name: "",
    banner_image_error: false,
    banner_image_mob: "",
    banner_image_mob_name: "",
    banner_image_mob_error: false,
  });

  // Section state
  const [sectionData, setSectionData] = useState({
    get_involved_section_id: 0,
    get_involved_section_title: "",
    get_involved_section_title_error: false,
    get_involved_section_title_maxlenth: 30,
    get_involved_des: "",
    get_involved_des_error: false,
    get_involved_des_maxlenth: 380,
  });

  // Image previews
  const [deskImage, setDeskImage] = useState("");
  const [mobImage, setMobImage] = useState("");

  // Handle tab changes
  const handleTabChange = (event, newValue) => {
    setErrorMsg("");
    setSuccessMsg("");
    setLoading(false);
    setActiveTab(newValue);
  };

  // Handle image changes
  const handleDeskImageChange = (event) => {
    setErrorMsg("");
    setSuccessMsg("");

    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setDeskImage(URL.createObjectURL(file));
      setBannerData({
        ...bannerData,
        banner_image: file,
        banner_image_name: file.name,
        banner_image_error: false,
      });
    }
  };

  const handleMobImageChange = (event) => {
    setErrorMsg("");
    setSuccessMsg("");

    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setMobImage(URL.createObjectURL(file));
      setBannerData({
        ...bannerData,
        banner_image_mob: file,
        banner_image_mob_name: file.name,
        banner_image_mob_error: false,
      });
    }
  };

  const clearUpload = (field, clearPreview) => {
    setBannerData((prev) => ({
      ...prev,
      [field]: "",
      [`${field}_name`]: "",
    }));
    clearPreview("");
  };

  // Handle text field changes
  const handleBannerChange = (event) => {
    setErrorMsg("");
    setSuccessMsg("");

    const { name, value } = event.target;

    if (
      name === "banner_title" &&
      value.length <= bannerData.banner_title_maxlenth
    ) {
      setBannerData({
        ...bannerData,
        banner_title: value,
        banner_title_error: false,
      });
    }
  };

  const handleSectionChange = (event) => {
    setErrorMsg("");
    setSuccessMsg("");

    const { name, value } = event.target;

    if (
      name === "get_involved_section_title" &&
      value.length <= sectionData.get_involved_section_title_maxlenth
    ) {
      setSectionData({
        ...sectionData,
        get_involved_section_title: value,
        get_involved_section_title_error: false,
      });
    } else if (
      name === "get_involved_des" &&
      value.length <= sectionData.get_involved_des_maxlenth
    ) {
      setSectionData({
        ...sectionData,
        get_involved_des: value,
        get_involved_des_error: false,
      });
    }
  };

  // Submit banner form
  const submitBanner = () => {
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    // Validate
    if (!bannerData.banner_title) {
      setBannerData({
        ...bannerData,
        banner_title_error: true,
      });
      setLoading(false);
      return;
    }

    // Create form data
    const formData = new FormData();
    formData.append("banner_section_id", bannerData.banner_section_id);
    formData.append("banner_image_mob", bannerData.banner_image_mob);
    formData.append("banner_image", bannerData.banner_image);
    formData.append("banner_title", bannerData.banner_title);

    // Submit to API
    addEditGetInvolved(formData)
      .then((res) => {
        if (res.data.status === true) {
          const isNew = bannerData.banner_section_id === 0;
          setSuccessMsg(
            isNew
              ? "Banner Added Successfully!"
              : "Banner Updated Successfully!",
          );

          // Update state with response if needed
          if (res.data.data) {
            const updatedData = { ...bannerData };
            updatedData.banner_section_id =
              res.data.data.banner_section_id || bannerData.banner_section_id;
            updatedData.banner_title =
              res.data.data.banner_title || bannerData.banner_title;

            if (res.data.data.banner_image) {
              updatedData.banner_image_name = res.data.data.banner_image;
            }

            if (res.data.data.banner_image_mob) {
              updatedData.banner_image_mob_name =
                res.data.data.banner_image_mob;
            }

            setBannerData(updatedData);
          }

          setTimeout(() => setSuccessMsg(""), 3000);
        } else {
          setErrorMsg(
            res.data.message || "Something went wrong, please try again!",
          );
        }
      })
      .catch(() => {
        setErrorMsg("Something went wrong, please try again!");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Submit section form
  const submitSection = () => {
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    // Validate
    let hasError = false;
    const updatedSection = { ...sectionData };

    if (!sectionData.get_involved_section_title) {
      updatedSection.get_involved_section_title_error = true;
      hasError = true;
    }

    if (!sectionData.get_involved_des) {
      updatedSection.get_involved_des_error = true;
      hasError = true;
    }

    if (hasError) {
      setSectionData(updatedSection);
      setLoading(false);
      return;
    }

    // Create form data
    const formData = new FormData();
    formData.append(
      "get_involved_section_id",
      sectionData.get_involved_section_id,
    );
    formData.append(
      "get_involved_section_title",
      sectionData.get_involved_section_title,
    );
    formData.append("get_involved_des", sectionData.get_involved_des);

    // Submit to API
    addEditGetInvolvedsection(formData)
      .then((res) => {
        if (res.data.status === true) {
          const isNew = sectionData.get_involved_section_id === 0;
          setSuccessMsg(
            isNew
              ? "Introduction Added Successfully!"
              : "Introduction Updated Successfully!",
          );

          // Update state with response if needed
          if (res.data.data) {
            const updatedData = { ...sectionData };
            updatedData.get_involved_section_id =
              res.data.data.get_involved_section_id ||
              sectionData.get_involved_section_id;
            updatedData.get_involved_section_title =
              res.data.data.get_involved_section_title ||
              sectionData.get_involved_section_title;
            updatedData.get_involved_des =
              res.data.data.get_involved_des || sectionData.get_involved_des;

            setSectionData(updatedData);
          }

          setTimeout(() => setSuccessMsg(""), 3000);
        } else {
          setErrorMsg(
            res.data.message || "Something went wrong, please try again!",
          );
        }
      })
      .catch(() => {
        setErrorMsg("Something went wrong, please try again!");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Load initial data
  useEffect(() => {
    // Get banner data
    getInvolvedBanner()
      .then((res) => {
        if (res.data.status === true && res.data.data) {
          // Update banner data directly
          const data = res.data.data;
          setBannerData({
            ...bannerData,
            banner_section_id: data.banner_section_id || 0,
            banner_title: data.banner_title || "",
            banner_image_name: data.banner_image || "",
            banner_image_mob_name: data.banner_image_mob || "",
          });

          // Set image previews
          if (data.banner_image) {
            setDeskImage(
              pathJoinUrl(storage[0].imageEndUrl, data.banner_image),
            );
          }

          if (data.banner_image_mob) {
            setMobImage(
              pathJoinUrl(storage[0].imageEndUrl, data.banner_image_mob),
            );
          }
        }
      })
      .catch((error) => {
        log.error("Failed to load banner data:", error);
      });

    // Get section data
    getInvolvedsection()
      .then((res) => {
        log.debug("Section data response:", res.data);

        if (res.data.status === true && res.data.data) {
          // Update section data directly
          const data = res.data.data;
          setSectionData({
            ...sectionData,
            get_involved_section_id: data.get_involved_section_id || 0,
            get_involved_section_title: data.get_involved_section_title || "",
            get_involved_des: data.get_involved_des || "",
          });

          log.debug("Section data set:", data);
        }
      })
      .catch((error) => {
        log.error("Failed to load section data:", error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ToolBody>
      <TabContext value={activeTab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList onChange={handleTabChange} aria-label="get-involved-tabs">
            <Tab label="Banner" value="1" />
            <Tab label="Introduction Section" value="2" />
          </TabList>
        </Box>

        {/* Banner Tab */}
        <TabPanel value="1">
          <Stack spacing={3}>
            <Box sx={{ maxWidth: 400 }}>
              <LimitedTextField
                id="banner_title"
                name="banner_title"
                label="Title"
                value={bannerData.banner_title}
                onChange={handleBannerChange}
                maxLength={bannerData.banner_title_maxlenth}
                error={bannerData.banner_title_error}
                errorText="Please enter Title!"
              />
            </Box>

            <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
              <Box sx={{ width: "100%" }}>
                <FileUploadArea
                  title="Add Banner Image For Desktop"
                  description={
                    <>
                      Best image Resolution: 1920px X 836px
                      <br />
                      Supported files types: JPG, JPEG & PNG
                    </>
                  }
                  previewUrl={deskImage}
                  fileName={bannerData.banner_image_name}
                  onChange={handleDeskImageChange}
                  deleteRequest={{
                    resource: "get-involved-banner",
                    id: bannerData.banner_section_id,
                    field: "banner_image",
                  }}
                  onRemove={() => clearUpload("banner_image", setDeskImage)}
                  accept=".png,.jpg,.jpeg"
                />
              </Box>

              <Box sx={{ width: "100%" }}>
                <FileUploadArea
                  title="Add Banner Image For Mobile"
                  description={
                    <>
                      Best image Resolution: 400px X 350px
                      <br />
                      Supported files types: JPG, JPEG & PNG
                    </>
                  }
                  previewUrl={mobImage}
                  fileName={bannerData.banner_image_mob_name}
                  onChange={handleMobImageChange}
                  deleteRequest={{
                    resource: "get-involved-banner",
                    id: bannerData.banner_section_id,
                    field: "banner_image_mob",
                  }}
                  onRemove={() =>
                    clearUpload("banner_image_mob", setMobImage)
                  }
                  accept=".png,.jpg,.jpeg"
                />
              </Box>
            </Stack>

            <FormActionBar
              onSubmit={submitBanner}
              isSubmitting={loading}
              isEdit={bannerData.banner_section_id !== 0}
              successMessage={successMsg}
              errorMessage={errorMsg}
              submitLabel="Update"
            />
          </Stack>
        </TabPanel>

        {/* Introduction Tab */}
        <TabPanel value="2">
          <Stack spacing={3}>
            <Box sx={{ maxWidth: 400 }}>
              <LimitedTextField
                id="get_involved_section_title"
                name="get_involved_section_title"
                label="Title"
                value={sectionData.get_involved_section_title}
                onChange={handleSectionChange}
                maxLength={sectionData.get_involved_section_title_maxlenth}
                error={sectionData.get_involved_section_title_error}
                errorText="Please enter Title!"
              />
            </Box>

            <Box>
              <LimitedTextField
                id="get_involved_des"
                name="get_involved_des"
                label="Short Description"
                value={sectionData.get_involved_des}
                onChange={handleSectionChange}
                maxLength={sectionData.get_involved_des_maxlenth}
                error={sectionData.get_involved_des_error}
                errorText="Please enter Short Description!"
                multiline
                rows={3}
              />
            </Box>

            <FormActionBar
              onSubmit={submitSection}
              isSubmitting={loading}
              isEdit={sectionData.get_involved_section_id !== 0}
              successMessage={successMsg}
              errorMessage={errorMsg}
              submitLabel="Update"
            />
          </Stack>
        </TabPanel>
      </TabContext>
    </ToolBody>
  );
};

export default GetInvolvedMembership;
